import { FaqSection } from './faqTypes';

// Commercial model, data lifecycle, and ownership. The retention tiers and the
// one-time versus recurring split are already in the pricing model; the
// one-time handover deliverable is the one thing that is neither specified nor
// built, and it is called out as such.

export const COMMERCIAL_SECTION: FaqSection = {
  id: 'commercial-model',
  title: 'Commercial model, retention, and ownership',
  intro:
    'One-time lines recover effort where it is incurred. Recurring lines are the annuity that keeps equipment funded after construction ends.',
  items: [
    {
      id: 'subscription-vs-onetime',
      question: 'Subscription model or one-time use?',
      status: 'decided',
      answer: [
        '**Both already exist in the model, and they do different jobs.**',
      ],
      bullets: [
        '**One-time lines recover effort at the point it is incurred:** onboarding per data source, the per-item data charge, mobilization for field services, and hardware.',
        '**Recurring lines are the annuity:** the platform base fee, the per-user fee, and post-handover retention.',
      ],
      note: '**Why the subscription matters:** construction ends, but the record does not. Revenue after handover is what keeps equipment amortization funded through the gap between projects, when utilization would otherwise be zero. **Recommendation:** default to a subscription during the project plus retention afterwards. Offer one-time only where a customer will not accept recurring, and price it higher to compensate for the annuity being given up.',
    },
    {
      id: 'onetime-end-product',
      question: 'For one-time use, what is the end product? Project log book, digital pipeline, or an analog record?',
      status: 'open',
      answer: [
        '**Not decided, and not built.** This is the largest gap between what the pricing model charges for and what a one-time customer would actually receive.',
        '**Recommendation: one named deliverable, the Project Record**, containing four things:',
      ],
      bullets: [
        '**The structured dataset.** Every pipe, joint, weld, inspection, and coating record with its source documents linked.',
        '**The document archive.** Original source files exactly as received.',
        '**The report set** generated during the project.',
        '**A readable index** that opens without the platform, so an auditor can use it unaided.',
      ],
      note: '**On the three options in the question:** a project log book is the readable narrative, a digital pipeline is the queryable asset model, and an analog record is a PDF pack. Ship the digital package with a PDF index. The PDF is what auditors accept, and the structured data is what makes the record worth keeping and makes warm access worth buying later.',
    },
    {
      id: 'data-lifecycle',
      question: 'Data retention: are there designated life cycles for data?',
      status: 'decided',
      answer: ['**Retention is priced per year, at a tier chosen per proposal.**'],
      bullets: [
        '**Cold archive, default $3,000 a year.** Immutable storage, retrieved on request, for audits and disputes. Ten years is $30,000.',
        '**Warm access, default $15,000 a year.** The application keeps running and the customer can log in and query at any time. Ten years is $150,000.',
      ],
      note: '**We do not enforce regulatory periods.** The model prices whatever number of years is entered, which keeps the compliance judgment with the customer. **Worth knowing when selling:** pipeline operators commonly retain material and weld records for the life of the asset, measured in decades. That is why retention is a real annuity rather than a rounding error.',
    },
    {
      id: 'data-ownership',
      question: 'Who owns the data?',
      status: 'decided',
      answer: ['**Four separate things, and confusing them causes most of the argument.**'],
      bullets: [
        '**The customer owns their source data and the project record** built from it. They commissioned it, it is their compliance record, and they must be able to take it with them.',
        '**CRC Evans owns the platform**, software, models, configuration, and documentation. This is already in the pricing sheet terms.',
        '**CRC Evans owns the hardware.** It is rented and returned in working condition at project end.',
        '**Anonymized and aggregated data may be used by CRC Evans** to improve the platform, with no customer-identifiable or project-sensitive content disclosed without written consent. Also already in the terms.',
      ],
      note: '**Recommendation:** say plainly that the customer owns their data. It costs nothing, it removes the biggest objection in the room, and the revenue sits in access, processing, and custody rather than in possession.',
    },
    {
      id: 'who-retains-data',
      question: 'Does the owner retain the data, or pay someone else to retain it?',
      status: 'decided',
      answer: ['**Both are legitimate, and the model prices for the second.**'],
      bullets: [
        '**They retain it themselves.** They take the handover package and there is no charge after project close. The annuity is lost, but the relationship stays clean.',
        '**They pay CRC Evans to retain it.** That is the retention line, cold or warm.',
      ],
      note: '**The case for paying us:** a compliance record is only useful if it stays readable and provably unaltered. Self-hosting an archive for decades means owning the format migration, the storage refresh, and the audit burden, usually with nobody internally assigned to it. Cold archive at $3,000 a year is cheaper than a person part-timing the same obligation. **Honest caveat:** cold archive is near pure margin by design, and a procurement team that compares it to raw cloud storage pricing will notice. Sell it as custody, retrieval, and audit assurance with a service commitment behind it, because as raw storage it is indefensible.',
    },
  ],
};
